import { AlertAction, IAlertProps } from "@components/alert/Alert.types";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SmartTable } from "@components/smart/smartTable";
import { TCustomRowAction } from "@components/smart/smartTable/SmartTable.utils";
import { IActionRendererArgs, IRow, TId } from "@components/table";
import { ActionCheckBoxStyled } from "@components/table/Table.styles";
import BindingContext, { createBindingContext } from "@odata/BindingContext";
import { ODataError } from "@odata/Data.types";
import { updateEntity } from "@odata/Data.utils";
import {
    EntitySetName,
    EntityTypeName,
    ICompanyRoleEntity,
    IUserCompanyRoleEntity,
    IUserEntity
} from "@odata/GeneratedEntityTypes";
import { CompanyRoleTypeCode } from "@odata/GeneratedEnums";
import { isBatchResultOk } from "@odata/OData";
import { isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import memoizeOneWithParamsCheck, { IMemoized } from "@utils/memoizeOne";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { BreadCrumbProvider } from "../../../components/breadCrumb";
import { ActionState, RowAction, Status, ToolbarItemType } from "../../../enums";
import { TRecordAny } from "../../../global.types";
import { ITableStorageDefaultCustomData } from "../../../model/TableStorage";
import { getAlertFromError } from "../../../views/formView/Form.utils";
import TableView from "../../../views/table";
import {
    ICustomButtonDefArgs,
    TableActionOrder,
    TableButtonsAction,
    TableButtonsActionType,
    TTableToolbarItem
} from "../../../views/table/TableToolbar.utils";
import { ITableViewBaseProps } from "../../../views/table/TableView";
import { getConfirmationActionText } from "../../../views/table/TableView.render.utils";
import { TableWrapper } from "../../../views/table/TableView.styles";
import SelectCompanyDialog from "./SelectCompanyDialog";
import { CustomerGeneralRoleId } from "./Users.utils";

export interface IUserTableCustomData extends ITableStorageDefaultCustomData {
    roleItems?: ISelectItem[];
    roleToAddId?: number;
    selectedCompaniesIds?: number[];
    selectCompanyDialogOpened?: boolean;
}

interface IProps extends ITableViewBaseProps<IUserTableCustomData> {
    customFooter?: ReactElement;
    customHeader?: ReactElement;
}

const MASS_ADD_ROLE_ACTION = "massAddRole";

class UsersTableView extends TableView<IProps> {

    constructor(props: IProps) {
        super(props);

        this.getConfirmText = this.getConfirmText.bind(this);
        this.getToolbarButtons = this.getToolbarButtons.bind(this);
        this.getCustomButtonDef = this.getCustomButtonDef.bind(this);
        this.renderSmartHeader = this.renderSmartHeader.bind(this);
        this.getTableSharedProps = this.getTableSharedProps.bind(this);
        this.render = this.render.bind(this);
    }

    componentDidMount() {
        this.loadRoles().then(() => this.forceUpdate());
        super.componentDidMount();
    }

    loadRoles = async (): Promise<void> => {
        try {
            const query = this.props.storage.oData.getEntitySetWrapper(EntitySetName.CompanyRoles).query();

            if (this.isAddingNewCompany) {
                query.filter(`TypeCode ne '${CompanyRoleTypeCode.CustomerRole}'`);
            }

            const res = await query.fetchData<ICompanyRoleEntity[]>();

            const roles = res.value;
            const roleItems = roles.map(role => {
                return {
                    id: `${MASS_ADD_ROLE_ACTION}_${role.Id}`,
                    label: role.CompanyRoleName,
                    additionalData: role
                };
            });

            this.props.storage.setCustomData({
                roleItems
            });
        } catch (e) {
            logger.error(e.toString());
        }
    };

    get isAddingNewCompany(): boolean {
        return this.context.getAddingNewCompany();
    }

    get isMassAddRoleAction(): boolean {
        return this.getTableAction()?.startsWith(MASS_ADD_ROLE_ACTION);
    }

    massAddRoleActionData = memoizeOneWithParamsCheck((): ICompanyRoleEntity => {
        const [action, roleId] = this.getTableAction()?.split("_") ?? [];
        if (action !== MASS_ADD_ROLE_ACTION || !roleId) {
            return null;
        }
        const intId = parseInt(roleId);
        const roleItems = this.props.storage.getCustomData().roleItems;
        return roleItems?.find(item => item.additionalData.Id === intId)?.additionalData ?? null;
    }, () => [this.getTableAction()]);

    getCustomButtonDef(type: TableButtonsActionType, args: ICustomButtonDefArgs): TTableToolbarItem {
        if (type === MASS_ADD_ROLE_ACTION) {
            const roleItems = this.props.storage.getCustomData().roleItems;
            const isActive = this.isMassAddRoleAction;
            const isAnotherActionActive = this.isTableAction() && !isActive;
            const isDisabled = isAnotherActionActive || args?.isDisabled;

            return {
                id: MASS_ADD_ROLE_ACTION,
                itemType: ToolbarItemType.IconSelect,
                iconName: "AddUserRole",
                label: this.props.storage.t("Users:AssignRole"),
                order: TableActionOrder.Remove + 1,
                isActive,
                isDisabled,
                itemProps: {
                    isTransparent: true,
                    headerText: i18next.t("Users:AssignRole"),
                    selectProps: {
                        isActive,
                        isDisabled: !roleItems?.length || isDisabled
                    },
                    items: roleItems
                }
            };
        }
        return super.getCustomButtonDef(type, args);
    }

    /**
     * Get initial active rows that already have the selected role. Used in create company wizard, where companyId is set,
     * and we need to preselect users with the selected role in that company.
     */
    getInitialActiveRows = memoizeOneWithParamsCheck((): BindingContext[] => {
        const rows = Object.values(this.props.storage.tableAPI?.getState().rows ?? []);
        const addRole = this.massAddRoleActionData();
        const companyId = this.context.getCompanyId();
        if (!companyId || !addRole?.Id) {
            return [];
        }
        // rows that already have the selected role
        return rows.filter(row => {
            return row?.customData?.entity.CompanyRoles?.some((role: IUserCompanyRoleEntity) => {
                return role.CompanyRole?.Id === addRole?.Id && role.Company.Id === companyId;
            });
        }).map(row => row.id as BindingContext);
    }, () => [this.massAddRoleActionData(), this.context.getCompanyId()]);

    getCustomRowAction: IMemoized<[], TCustomRowAction> = memoizeOneWithParamsCheck((): TCustomRowAction => {
        if (this.isMassAddRoleAction && this.isAddingNewCompany) {
            const render = (args: IActionRendererArgs) => {
                const isCheckedDisabled = args.actionState === ActionState.Disabled && this.props.storage.tableAPI?.getState().activeRows.has(args.rowId?.toString());
                return <ActionCheckBoxStyled checked={args.actionState === ActionState.Active || isCheckedDisabled}
                                             isDisabled={args.isDisabled}
                                             onChange={args.onClick}/>;
            };
            return {
                actionType: RowAction.Custom,
                render
            };
        }

        return null;
    }, () => [this.getTableAction()]);

    isRowWithoutAction(rowId: TId, action: RowAction, row: IRow): boolean {
        if (action === RowAction.Custom && this.isMassAddRoleAction) {
            const roleToAddTypeCode = this.massAddRoleActionData().TypeCode;
            const isCustomerRow = row.customData.entity?.GeneralRole?.Id === CustomerGeneralRoleId;
            const addingCustomerRole = roleToAddTypeCode === CompanyRoleTypeCode.CustomerRole;
            if (isCustomerRow !== addingCustomerRole) {
                return true;
            }
        }

        return super.isRowWithoutAction(rowId, action, row);
    }

    getConfirmText(tableAction: TableButtonsAction | string): string | React.ReactElement {
        if (tableAction?.startsWith(MASS_ADD_ROLE_ACTION)) {
            const count = this.props.storage.tableAPI?.getState().activeRows.size ?? 0;
            return getConfirmationActionText(this.props.storage.t("Users:Assign"), count);
        }
        return super.getConfirmText(tableAction);
    }

    getToolbarButtons(): TableButtonsActionType[] {
        const buttons = super.getToolbarButtons();
        buttons.push(MASS_ADD_ROLE_ACTION);
        return buttons;
    }

    assignRolesToUsers = async (): Promise<void | IAlertProps> => {
        const storage = this.props.storage;
        const { selectedCompaniesIds } = storage.getCustomData();
        const roleToAddId = this.massAddRoleActionData().Id;
        const users = storage.tableAPI?.getState().activeRows;
        const rows = storage.tableAPI?.getState().rows;

        const batch = storage.oData.batch();

        const removedUsers = this.getInitialActiveRows().filter(bc => !users.has(bc.toString()));

        for (const user of users) {
            const userEntity = cloneDeep(rows[user]?.customData.entity) as IUserEntity;

            for (const companyId of selectedCompaniesIds) {
                const userRole = userEntity.CompanyRoles.find(role => role.Company.Id === companyId);
                if (userRole) {
                    userRole.CompanyRole = {
                        Id: roleToAddId,
                    };
                } else {
                    userEntity.CompanyRoles.push({
                        Company: {
                            Id: companyId
                        },
                        CompanyRole: {
                            Id: roleToAddId
                        }
                    });
                }
            }

            updateEntity({
                batch,
                changesOnly: true,
                bindingContext: createBindingContext(EntitySetName.Users, storage.oData.getMetadata()).addKey(userEntity.Id),
                entity: userEntity,
                originalEntity: rows[user]?.customData.entity
            });
        }

        for (const user of removedUsers) {
            const userEntity = cloneDeep(rows[user.toString()]?.customData.entity) as IUserEntity;
            const userRole = userEntity.CompanyRoles.find(role => role.Company.Id === this.context.getCompanyId());
            if (userRole) {
                userEntity.CompanyRoles = userEntity.CompanyRoles.filter(role => role.Company.Id !== this.context.getCompanyId());
            }

            updateEntity({
                batch,
                changesOnly: true,
                bindingContext: createBindingContext(EntitySetName.Users, storage.oData.getMetadata()).addKey(userEntity.Id),
                entity: userEntity,
                originalEntity: rows[user.toString()]?.customData.entity
            });
        }

        let errors: string[] = [];
        if (!batch.isEmpty()) {
            const batchResponse = await batch.execute();

            for (const res of batchResponse) {
                if (!isBatchResultOk(res)) {
                    if (res.body instanceof ODataError || typeof res.body === "string") {
                        const resSubtitle = getAlertFromError(res.body).subTitle as string[];
                        const entityReference = res.body._validationMessages?.[0]?.entity;
                        if (entityReference.entityType === EntityTypeName.User && entityReference.id) {
                            const bc = createBindingContext(EntitySetName.Users, storage.oData.getMetadata()).addKey(entityReference.id);
                            const userEntity = rows[bc.toString()]?.customData?.entity as IUserEntity;
                            if (!isObjectEmpty(userEntity)) {
                                errors.push(`${userEntity.FirstName} ${userEntity.LastName} - ${resSubtitle}`);
                            }
                        } else {
                            errors = [...errors, ...resSubtitle];
                        }
                    }
                }
            }
        }

        if (errors.length) {
            return {
                action: AlertAction.Close,
                status: Status.Error,
                useFade: false,
                title: storage.t("Users:AssignRoleFailed"),
                subTitle: errors?.join("\n")
            };
        } else {
            storage.data.alert = {
                status: Status.Success,
                title: storage.t("Components:Table.UpdateOk"),
                subTitle: storage.t("Users:AssignRoleSuccess"),
                useFade: true
            };
        }

        await this.handleCloseSelectCompanyDialog();
    };

    handleToolbarConfirm = async (): Promise<void> => {
        if (this.isMassAddRoleAction) {
            this.setState({
                isTableActionInProgress: true
            });
            this.props.storage.tableAPI.setState({ loaded: false });
            if (this.context.getAddingNewCompany()) {
                this.props.storage.setCustomData({
                    selectedCompaniesIds: [this.context.getCompanyId()]
                });
                await this.assignRolesToUsers();
            } else {
                this.props.storage.setCustomData({ selectCompanyDialogOpened: true });
            }
            this.setState({
                isTableActionInProgress: false
            });
            await this.props.storage.tableAPI.reloadTable();
        } else {
            await super.handleToolbarConfirm();
        }
    };

    handleCloseSelectCompanyDialog = async (): Promise<void> => {
        this.props.storage.setCustomData({ selectCompanyDialogOpened: false });
        await this.setTableAction(null);
        this.forceUpdate();
    };

    renderSmartHeader(): React.ReactElement {
        return this.props.customHeader ?? super.renderSmartHeader();
    }

    renderCustomFooter = (): ReactElement => {
        return this.props.customFooter;
    };

    renderSelectCompanyDialog = (): ReactElement => {
        if (!this.props.storage.getCustomData().selectCompanyDialogOpened) {
            return null;
        }

        return (
                <SelectCompanyDialog
                        storage={this.props.storage}
                        onClose={this.handleCloseSelectCompanyDialog}
                        onConfirm={this.assignRolesToUsers}
                />
        );
    };

    renderTable(): React.ReactElement {
        return (<>
            <TableWrapper>
                <SmartTable {...this.getTableSharedProps()}
                            initialActiveRows={this.getInitialActiveRows()} />
            </TableWrapper>
            {this.renderCustomFooter()}
        </>);
    }

    renderBreadCrumbs(): React.ReactElement {
        return this.isAddingNewCompany ? (
                <BreadCrumbProvider/>
        ) : super.renderBreadCrumbs();
    }

    getViewProps(): TRecordAny {
        return this.isAddingNewCompany ? { style: { paddingBottom: 0 }} : {};
    }

    renderDefaultDialogs(): React.ReactElement {
        return (<>
            {super.renderDefaultDialogs()}
            {this.renderSelectCompanyDialog()}
        </>);
    }
}

export default UsersTableView;